import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, Shield, Users, Info, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/f617a5a9-dda7-40dc-bbb9-de762658adaf/images/5d32d876981551b4c98ceec76bfdad1e26bc99093c09f2a5f2242b603b114d79.png";

export default function SignupPage({ setUser }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState('viewer');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const payload = { 
        name, 
        email, 
        password,
        role: accountType === 'league_admin' ? 'admin' : 'viewer',
        admin_type: accountType === 'league_admin' ? 'league_admin' : null
      };
      
      console.log('Signup payload:', payload);
      console.log('API URL:', API);
      
      const response = await axios.post(`${API}/auth/signup`, payload);
      console.log('Signup response:', response.data);
      
      toast.success('Account created successfully! Please log in.');
      navigate('/login');
    } catch (error) {
      console.error('Signup error:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.detail || error.message || 'Signup failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D5A5A] to-[#1E4545] flex flex-col">
      {/* Header */}
      <div className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
          Back
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-3xl shadow-2xl border-0">
          <CardContent className="p-8">
            {/* Logo */}
            <div className="text-center mb-6">
              <img src={LOGO_URL} alt="CricORama" className="w-20 h-20 object-contain mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 font-display">Create Account</h1>
              <p className="text-slate-500 mt-1">Join CricORama</p>
            </div>

            {/* Account Type Selection */}
            <div className="mb-6">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">Choose your account type:</Label>
              <RadioGroup value={accountType} onValueChange={setAccountType} className="space-y-3">
                {/* Viewer Option */}
                <div>
                  <RadioGroupItem value="viewer" id="viewer" className="peer sr-only" />
                  <Label
                    htmlFor="viewer"
                    className="flex items-start gap-4 rounded-xl border-2 border-slate-200 bg-white p-4 cursor-pointer hover:border-[#5D8A8A] peer-data-[state=checked]:border-[#2D5A5A] peer-data-[state=checked]:bg-[#2D5A5A]/5 transition-all"
                  >
                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-[#2D5A5A]" />
                    </div>
                    <div className="flex-1">
                      <span className="text-base font-semibold text-slate-900 block">Fan / Viewer</span>
                      <span className="text-sm text-slate-500 mt-1 block">
                        Follow live matches, view scores, standings, player stats, and league updates. Perfect for cricket enthusiasts!
                      </span>
                    </div>
                  </Label>
                </div>

                {/* League Admin Option */}
                <div>
                  <RadioGroupItem value="league_admin" id="league_admin" className="peer sr-only" />
                  <Label
                    htmlFor="league_admin"
                    className="flex items-start gap-4 rounded-xl border-2 border-slate-200 bg-white p-4 cursor-pointer hover:border-[#8B1E3F] peer-data-[state=checked]:border-[#8B1E3F] peer-data-[state=checked]:bg-[#8B1E3F]/5 transition-all"
                  >
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-[#8B1E3F]" />
                    </div>
                    <div className="flex-1">
                      <span className="text-base font-semibold text-slate-900 block">League Admin</span>
                      <span className="text-sm text-slate-500 mt-1 block">
                        Create and manage your own leagues, teams, players, and match schedules. Assign scorers and run tournaments.
                      </span>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              {/* Scorer Info Box */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <Award className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-amber-800">Looking to be a Scorer?</span>
                  <p className="text-amber-700 mt-0.5">
                    Scorer accounts are created by League Admins. Contact your league administrator to get scorer access for live match scoring.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-12 h-12 rounded-xl"
                    required
                    data-testid="signup-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 h-12 rounded-xl"
                    required
                    data-testid="signup-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-12 pr-12 h-12 rounded-xl"
                    required
                    data-testid="signup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-12 h-12 rounded-xl"
                    required
                    data-testid="signup-confirm-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/30"
                disabled={loading}
                data-testid="signup-submit"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-orange-500 font-semibold hover:underline">
                  Sign In
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
